import {
  type DocumentVersion,
  prepareDocumentForSave
} from "@prismicio/editor-fields"
import { useRequest } from "@prismicio/editor-support/Suspense"
import { Button, Tooltip, useAlert } from "@prismicio/editor-ui"
import { useTransition } from "react"

import { getAllReleases, getReleaseId } from "../../../app/services/release"
import { type Role, useRole } from "../../../app/services/user"
import { saveDocument } from "../../documentData"
import { useExistingDocument } from "../../DocumentProvider"
import { useGoToDocumentVersion } from "../../documentRouting"
import { validateUID } from "../validateUID"
import { actionAlert, type PendingActionProps } from "./HeaderButtons"

interface SaveButtonProps extends PendingActionProps {}

export function SaveButton(props: SaveButtonProps) {
  const { pendingAction } = props
  const { disabled, save, roleCanSave, tooltip } = useSaveButton(props)

  return (
    <Tooltip
      visible={!roleCanSave}
      title={tooltip.title}
      content={tooltip.content}
    >
      <Button
        size="large"
        color="grey"
        loading={pendingAction === "save"}
        disabled={disabled}
        onClick={save}
      >
        Save
      </Button>
    </Tooltip>
  )
}

function useSaveButton(props: SaveButtonProps) {
  const { pendingAction, onPendingActionChange } = props

  const { content, draft, edited, data, locale } = useExistingDocument()

  const { meta, version, customType } = data

  const [isUpdating, startTransition] = useTransition()
  const goToDocumentVersion = useGoToDocumentVersion()
  const { showAlert } = useAlert()

  const { role } = useRole(locale)

  const releases = useRequest(getAllReleases, [])

  const isScheduledForRelease = releases.some(
    (release) => release.id === getReleaseId(data.version) && release.timestamp
  )

  const roleCanSave = canSave(role, isScheduledForRelease)

  const disabled =
    !edited || Boolean(pendingAction) || isUpdating || !roleCanSave

  async function save() {
    onPendingActionChange("save")

    const isValidUID = await validateUID({
      content,
      documentId: meta.id,
      languageId: meta.language.id,
      customType,
      showAlert,
      pendingAction: "save",
      onRetry: save
    })

    if (!isValidUID) {
      onPendingActionChange(undefined)
      return
    }

    const baseVersion = getBaseVersion(version, meta.versions)
    if (!baseVersion) return

    const releaseId = getReleaseId(baseVersion)

    const documentWithDefaultValues = prepareDocumentForSave(
      customType,
      content
    )

    saveDocument({
      id: meta.id,
      baseVersionId: baseVersion.version_id,
      releaseId,
      documentTags: draft.documentTags,
      content: documentWithDefaultValues,
      status: baseVersion.status
    })
      .then((newVersion) => {
        startTransition(() =>
          goToDocumentVersion({
            status: newVersion.status,
            releaseId,
            preventScrollReset: true
          })
        )
      })
      .catch(() => actionAlert({ action: "save", showAlert, onRetry: save }))
      .finally(() => onPendingActionChange(undefined))
  }

  return {
    disabled,
    save,
    roleCanSave,
    tooltip: getTooltip(isScheduledForRelease)
  }
}

function getBaseVersion(version: DocumentVersion, versions: DocumentVersion[]) {
  // Till we fully implement conflict resolution, just save the current draft -
  // using any existing draft as a base version.
  if (version.status !== "published") return version

  const firstDraft = versions.find(
    (version) => version.status === "unclassified"
  )

  return firstDraft ?? version
}

function canSave(role: Role, isScheduledForRelease: boolean) {
  const savePermission: Record<Role, boolean> = {
    SuperUser: true,
    Owner: true,
    Administrator: true,
    Publisher: true,
    Writer: !isScheduledForRelease,
    Contributor: false
  }

  return savePermission[role]
}

function getTooltip(isScheduledForRelease: boolean) {
  if (isScheduledForRelease)
    return {
      title: "You can't update documents scheduled for release",
      content:
        "Only users with the role of publisher or administrator can update documents scheduled for release."
    }

  return {
    title: "You can't update documents",
    content:
      "Only users with the role of publisher or administrator can update documents."
  }
}
